"use client"

import { useRef, useState, useEffect } from "react"
import NextImage from "next/image"
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion"
import { ArrowRight } from "lucide-react"
import Link from "next/link"
import Esg from "@/public/HeroReportsThumbnail/Esg.png"
import Sustanability from "@/public/HeroReportsThumbnail/Sustanability.png"
import Godrej from "@/public/HeroReportsThumbnail/Godrej.png"
import AnnualReport from "@/public/HeroReportsThumbnail/AnnualReport.png"

// Sample report data with monochrome scheme
const reportShowcases = [
  {
    id: 1,
    title: "Annual Financial Report",
    client: "Adani Ports",
    image: AnnualReport,
  },
  {
    id: 2,
    title: "Sustainability Impact",
    client: "Bharat Forge",
    image: Sustanability,
  },
  {
    id: 3,
    title: "Integrated Annual Review",
    client: "Godrej Properties",
    image: Godrej,
  },
  {
    id: 4,
    title: "ESG Report",
    client: "Adani Renewables",
    image: Esg,
  },
]

export default function HeroSection() {
  const [currentReport, setCurrentReport] = useState(0)
  const heroRef = useRef<HTMLDivElement>(null)
  const reportContainerRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  })

  // Parallax effects
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.8])

  // Auto-rotate reports
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentReport((prev) => (prev + 1) % reportShowcases.length)
    }, 5000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  // Handle manual navigation
  const handleReportChange = (index: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    setCurrentReport(index)

    // Restart interval
    intervalRef.current = setInterval(() => {
      setCurrentReport((prev) => (prev + 1) % reportShowcases.length)
    }, 5000)
  }

  return (
    <section ref={heroRef} className="relative min-h-screen overflow-hidden border-b bg-gray-100 pt-14 md:pt-0">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('/placeholder.svg?height=1000&width=1000')] bg-no-repeat bg-cover opacity-[0.02]"></div>
        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-b from-gray-100/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-gray-100/30 to-transparent"></div>
      </div>

      {/* Content container */}
      <div className="container mx-auto px-4 relative z-10 min-h-screen flex flex-col lg:flex-row items-center py-10 md:py-0">
        {/* Left column - Text content */}
        <motion.div
          className="w-full lg:w-1/2 pt-0 lg:pt-0 mb-10 lg:mb-0"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="max-w-xl">
            {/* <motion.div
              className="inline-block md:mb-6 mb-4 overflow-hidden"
              initial={{ width: 0 }}
              animate={{ width: "auto" }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h1 className="block px-4 py-2 bg-gray-200 text-gray-800 font-medium text-sm rounded-full whitespace-nowrap shadow-sm">
                ESG & Sustainability Report Design Agency
              </h1>
            </motion.div> */}

            <motion.h1 style={{ lineHeight: "1.3" }}
              className="text-4xl md:text-5xl font-bold text-gray-900 md:mb-6 mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            > Sustainability & Annual Report Design Agency
              {/* Transform Your Corporate Reports Into <span className="text-black"> Visual Excellence</span> */}
              {/* Master Your <br /> <span className="text-black">ESG Storytelling</span> */}
            </motion.h1>

            <motion.p
              className="text-lg text-gray-600 md:mb-8 mb-6 pr-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Your sustainability reports are more
              than compliance documents.
              Annual ESG turns complex data
              into beautifully designed
              Sustainability and Annual reports
              that engage stakeholders without
              compromising on your Brand
              Assets. We change your corporate
              reports into a Stakeholder Magnet -
              Without Compromising Your
              Brand.
            </motion.p>

            <motion.div
              className="flex flex-wrap md:justify-start justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link
                href="#contact"
                className="bg-black hover:bg-gray-900 text-white px-8 py-4 rounded-full text-lg font-medium shadow-lg transition-all duration-300 flex items-center group"
              >
                Start Your Report
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                href="#portfolio"
                className="border border-gray-300 hover:border-gray-400 text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-8 py-4 text-lg rounded-full transition-all duration-300 shadow-sm"
              >
                View Our Work
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* Right column - Report showcase */}
        <motion.div
          className="w-full lg:w-1/2 h-[400px] md:h-[500px] lg:h-full relative flex items-center justify-center"
          style={{ opacity }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          {/* 3D perspective container */}
          <div ref={reportContainerRef} className="relative w-full h-full max-w-2xl flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentReport}
                initial={{ opacity: 0, rotateY: -15, scale: 0.95 }}
                animate={{ opacity: 1, rotateY: 0, scale: 1 }}
                exit={{ opacity: 0, rotateY: 15, scale: 0.95 }}
                transition={{ duration: 0.7, ease: "easeInOut" }}
                className="relative w-full h-full flex items-center justify-center"
              >
                {/* Main report */}
                <div className="relative w-[250px] h-[350px] md:w-[300px] md:h-[420px] lg:w-[350px] lg:h-[500px] shadow-2xl rounded-lg overflow-hidden">
                  <NextImage
                    src={reportShowcases[currentReport].image}
                    alt={reportShowcases[currentReport].title}
                    fill
                    className="object-cover"
                    priority
                  />

                  {/* <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-gray-800/60"></div> */}
                  {/*<div className="absolute inset-0 bg-white/5 backdrop-blur-[1px]"></div> */}

                  {/* Report info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/60 to-black/80"></div>
                    <motion.div
                      initial={{ opacity: 1, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      className="relative text-center md:text-left"
                    >
                      <div className="text-white text-sm font-medium uppercase tracking-wider z-10">
                        {reportShowcases[currentReport].client}
                      </div>
                      <div className="text-3xl text-white font-bold mt-1 z-10">{reportShowcases[currentReport].title}</div>
                    </motion.div>
                  </div>

                  {/* Decorative elements */}
                  {/* <motion.div
                    className="absolute top-8 right-8 w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                    transition={{ duration: 4, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
                  />
                  <motion.div
                    className="absolute bottom-32 left-4 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut", delay: 1 }}
                  /> */}
                </div>

                {/* Floating page elements */}
                <motion.div
                  className="absolute top-[20%] -right-[5%] w-[120px] h-[160px] md:w-[150px] md:h-[200px] lg:w-[180px] lg:h-[240px] bg-white/90 backdrop-blur-sm shadow-xl rounded-lg overflow-hidden z-10 border border-gray-200"
                  animate={{
                    y: [0, -15, 0],
                    rotate: [0, 2, 0],
                    x: [0, 5, 0],
                  }}
                  transition={{ duration: 6, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
                >
                  <div className="h-full w-full bg-gradient-to-br from-white to-gray-50 p-4">
                    <div className="w-full h-4 bg-gray-200 rounded mb-4"></div>
                    <div className="w-3/4 h-4 bg-gray-200 rounded mb-6"></div>
                    {/* <div className="w-full h-24 bg-gray-100 rounded mb-4"></div> */}
                    <NextImage
                      src={Godrej}
                      alt="image"
                      fill
                      className="object-cove h-24 rounded mb-3 w-full"
                      priority
                    />
                    <div className="w-full h-4 bg-gray-200 rounded mb-3"></div>
                  </div>
                </motion.div>

                <motion.div
                  className="absolute bottom-[15%] -left-[5%] w-[100px] h-[140px] md:w-[130px] md:h-[170px] lg:w-[160px] lg:h-[200px] bg-white/90 backdrop-blur-sm shadow-xl rounded-lg overflow-hidden z-10 border border-gray-200"
                  animate={{
                    y: [0, 15, 0],
                    rotate: [0, -3, 0],
                    x: [0, -5, 0],
                  }}
                  transition={{ duration: 6, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut", delay: 1 }}
                >
                  <div className="h-full w-full bg-gradient-to-br from-white to-gray-50 p-3">
                    {/* <div className="w-full h-20 bg-gray-100 rounded mb-3"></div> */}
                    <NextImage
                      src={AnnualReport}
                      alt="image"
                      fill
                      className="object-cove h-20 rounded mb-3 w-full"
                      priority
                    />
                    <div className="w-full h-3 bg-gray-200 rounded mb-2"></div>
                    <div className="w-4/5 h-3 bg-gray-200 rounded mb-2"></div>
                    <div className="w-full h-3 bg-gray-200 rounded mb-2"></div>
                    <div className="w-4/5 h-3 bg-gray-200 rounded mb-2"></div>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>

            {/* Report navigation dots - Positioned below the animation */}
            <motion.div
              className="flex items-center justify-center gap-3 absolute md:-bottom-12 -bottom-4 left-0 right-0 z-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              {reportShowcases.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleReportChange(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${currentReport === index ? "bg-black w-8 shadow-md" : "bg-gray-300 hover:bg-gray-400 shadow-sm"
                    }`}
                  aria-label={`View report ${index + 1}`}
                />
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}









